import { QesIcons } from "@app/shared/icons/qesIcons";

enum NetworkAssessmentAnalysisType {
  Unknown = "unknown",
  ServiceArea = "serviceArea",
  Opportunity = "opportunity",
  TermedServiceArea = "termedServiceArea",
  TermedOpportunity = "termedOpportunity",
  ProjectedServiceArea = "projectedServiceArea",
  ProjectedOpportunity = "projectedOpportunity",
  ProjectedTermedServiceArea = "projectedTermedServiceArea",
  ProjectedTermedOpportunity = "projectedTermedOpportunity",
}

enum AdequacyType {
  MedicareAdvantage = 1,
  CCIIOQHP = 2,
  NYSDOH = 3,
  PACAID = 4,
  CACDI = 5,
  CADMHC = 6,
  NJDOBI = 7,
  NPI = 8,
  ILCAID = 9,
  TXCAID = 10,
  NMCAID = 11,
  NJDMAHS = 12,
}

enum FilterType {
  Band = "/band/",
  County = "/county/",
  Inspection = "/inspection/",
  Region = "/region/",
  Specialty = "/specialty/",
  State = "/state/",
  Zip = "/zip/",
}

enum CalculationMethod {
  Distance = 0,
  Time = 1,
  TimeAndDistance = 2,
  TimeOrDistance = 3,
}

enum ProjectExclusionType {
  /**
   * Provider is not excluded
   */
  None = "none",

  /**
   * Facility is excluded due to Deactivated NPI.  Marty says this enum value may not remain - a single DeactivatedNPI enum value is probably sufficient
   */
  DeactivatedFacility = "deactivatedFacility",

  /**
   * Provider is excluded due to Deactivated NPI
   */
  DeactivatedProvider = "deactivatedProvider",

  /**
   * Provider is excluded due to Medicare Opt-Out
   */
  MedicareOptOut = "medicareOptOut",

  /**
   * Provider is excluded due to OIG Exclusion
   */
  OIGExclusion = "oigExclusion",

  /**
   * Specifies that a provider has an OIG Exclusion waiver
   */
  OIGExclusionWaiver = "oigExclusionWaiver",

  /**
   * Provider is precluded from receiving payment.
   */
  Precluded = "precluded",

  /**
   * Facility is excluded due to invalid NPPES NPIs
   */
  InvalidNppesNpiFacility = "invalidNppesNpiFacility",

  /**
 * Provider is excluded due to invalid NPPES NPIs
 */
  InvalidNppesNpiProvider = "invalidNppesNpiProvider",

  /**
   * Facility is excluded due to missing NPIs
   */
  MissingNpiFacility = "missingNpiFacility",

  /**
 * Provider is excluded due to missing NPIs
 */
  MissingNpiProvider = "missingNpiProvider",
}

enum ImpactIndicator {
  None = "none",
  EmptyDot = "emptyDot",
  FullDot = "fullDot",
}

enum NextClosestProviderAction {
  Unknown = "unknown",
  None = "none",
  FilterToNextClosestProvidersOnly = "filterToNextClosestProvidersOnly",
  AppendNextClosestProviders = "appendNextClosestProviders",
  FlterToZipAnalysisNextClosestProvidersOnly = "filterToZipAnalysisNextClosestProvidersOnly"
}

enum NPIStatus {
  Unknown = "unknown",
  Valid = "valid",
  Deactivated = "deactivated",
  Invalid = "invalid",
  Missing = "missing",
}

enum ProjectionProviderType {
  Unknown = "unknown",
  Target = "target",
  ExcludeImpact = "excludeImpact",
}

enum ProjectionProviderHistoryType {
  Unknown = "unknown",
  Created = "created",
  Cloned = "cloned",
  Standard = "standard",
}

enum ProjectPerspective {
  All = "ALL",
  ByCounty = "BY_COUNTY",
  BySpecialty = "BY_SPECIALTY",
  ByState = "BY_STATE",
  ByInspection = "BY_INSPECTION",
  ByRegion = "BY_REGION",
  ByZipCode = "BY_ZIP_CODE",
  ByZipClass = "BY_ZIP_CLASS",
}

enum ProviderActionStatusEnum {
  Unknown = "unknown",
  FullyTerminated = "fullyTerminated",
  PartiallyTerminated = "partiallyTerminated",
  FullyExcluded = "fullyExcluded",
  PartiallyExcluded = "partiallyExcluded",
  FullyTargeted = "fullyTargeted",
  PartiallyTargeted = "partiallyTargeted"
}

enum ProjectRunConfigurationStatus {
  Unknown = "unknown",
  Created = "created",
  Queued = "queued",
  Analyzing = "analyzing",
  Aggregating = "aggregating",
  Succeeded = "succeeded",
  Failed = "failed",
  Canceled = "canceled",
  QueuedProviderChanges = "queuedProviderChanges",
  ProcessingProviderChanges = "processingProviderChanges",
  QueuedSpecialtyGroupExceptionAnalysis = "queuedSpecialtyGroupExceptionAnalysis",
  ProcessingSpecialtyGroupExceptionAnalysis = "processingSpecialtyGroupExceptionAnalysis",
  FailedProviderChanges = "failedProviderChanges",
  QueuedNetworkNcp = "queuedNetworkNcp",
  ProcessingNetworkNcp = "processingNetworkNcp",
  FailedNetworkNcp = "failedNetworkNcp",
  CanceledNetworkNcp = "canceledNetworkNcp",
  QueuedProspectMarketNcp = "queuedProspectMarketNcp",
  ProcessingProspectMarketNcp = "processingProspectMarketNcp",
  FailedProspectMarketNcp = "failedProspectMarketNcp",
  CanceledProspectMarketNcp = "canceledProspectMarketNcp"
}

interface ProjectRunStatusObject {
  status: ProjectRunConfigurationStatus;
  statusSeverity: StatusSeverity;
  statusText: string;
}

// this is a newer enum for the separate ProjectRunConfigurationStatus table,
// first used for zip analysis projects
enum ProjectRunStatusEnum {
  Unknown = "unknown",
  Created = "created",
  Queued = "queued",
  Running = "running",
  // 4 is blank for backwards compatibility
  Succeeded = "succeeded",
  Failed = "failed",
  Canceled = "canceled",
  MissingData = "missingData"
}

// used to identify the type of job that a status is tracked for from
// the above enum
enum ProjectRunStatusTypeEnum {
  Unknown = "unknown",
  ProjectRunStatus = "projectRunStatus",
  SpecialtyGroupExceptionStatus = "specialtyGroupExceptionStatus",
  NcpStatus = "ncpStatus",
  ProjectionStatus = "projectionStatus",
  ServicingProviderIngestionStatus = "servicingProviderIngestionStatus",
  ScenarioModelingIngestionStatus = "scenarioModelingIngestionStatus"
}

enum StatusSeverity {
  Informational = "informational",
  Warning = "warning"
}

enum AccuracyGapAnalysisStatus {
  Unknown = "unknown",
  Queued = "queued",
  Analyzing = "analyzing",
  Succeeded = "succeeded",
  Failed = "failed",
  Canceled = "canceled",
}

enum ProjectRunConfigurationType {
  Unknown = "unknown",
  Standard = "standard",
  DualMembershipPrimary = "dualMembershipPrimary",
  DualMembershipSupplemental = "dualMembershipSupplemental",
}

enum ProjectRunConfigurationJobStatus {
  Unknown = "unknown",
  Queued = "queued",
  Processing = "processing",
  Loaded = "loaded",
  Purged = "purged",
  Failed = "failed",
}

enum ProviderActionType {
  NetNew = "netNew",
  Termination = "termination"
}

enum ProviderBadgeStyle {
  Outline = "outline",
  Dot = "dot",
}

enum RegionSetStatusType {
  Active = "Active",
  Approved = "Approved",
  Opportunity = "Opportunity",
  Pending = "Pending",
  Unknown = "Unknown",
}

enum ComparisonProviderType {
  Unknown = "Unknown",
  Added = "Added",
  Removed = "Removed",
  Matched = "Matched",
}

enum TimeFrequency {
  Unknown = "unknown",
  All = "all",
  Monthly = "monthly",
}

enum AttestationRateDays {
  Ninety = "90",
  ThreeSixtyFive = "365"
}

enum TimeDistanceCalculatorType {
  Unknown = "unknown",
  Quest = "quest",
  GeographyAware = "geographyAware",
  GraphhopperRouting = "graphhopperRouting",
  StraightLine = "straightLine",
}

enum AccuracyInspectionResultTypeEnum {
  Unknown = "Unknown",
  Name = "Name",
  Address = "Address",
  Phone = "Phone",
}

enum AccuracySupplementalTypeEnum {
  Valid = "valid",
  Unknown = "unknown",
  Enriched = "enriched",
  Invalid = "invalid",
}

enum PanelStatusEnum {
  Unknown = "unknown",
  AcceptingNewPatients = "acceptingNewPatients",
  NotAcceptingNewPatients = "notAcceptingNewPatients",
  SeeingEstablishedPatientsOnly = "seeingEstablishedPatientsOnly",
  PatientAgeRestriction = "patientAgeRestriction",
}

enum RecommendedActionTypeEnum {
  Unknown = "Unknown",
  Suppress = "Suppress",
  Investigate = "Investigate",
  Update = "Update",
  Retain = "Retain",
  Standardize = "Standardize",
  Enrich = "Enrich",
  Supplement = "Supplement"
}

enum ProjectOverrideMemberSetType {
  Primary = "Primary",
  Secondary = "Secondary",
  Alternate = "Alternate",
  Risk = "Risk Analysis",
}

enum AccuracyInspectionIconEnum {
  Ban = QesIcons.SlashOctagon,
  Check = QesIcons.Check,
  Question = QesIcons.Help,
  Star = QesIcons.StarV1,
  Times = QesIcons.X,
}

enum EntityTypeEnum {
  Unknown = "unknown",
  Individual = "individual",
  Facility = "facility",
}

enum AdequacyRowStatusEnum {
  "Unknown" = "unknown",
  "NoGap" = "noGap",
  "PartialGap" = "partialGap",
  "FullGap" = "fullGap",
  "ApprovedException" = "approvedException",
  "Empty" = "empty",
  "ZeroMembership" = "zeroMembership",
}

enum AccuracyBandEnum {
  Unknown = "Unknown",
  Accurate = "Accurate",
  Suspect = "Suspect",
  Inaccurate = "Inaccurate",
  Skipped = "Skipped",
  Enriched = "Enriched",
}

enum PatientVolumeLabelEnum {
  Unknown = "Unknown",
  Ghost = "Ghost",
  Peripheral = "Peripheral",
  Standard = "Standard",
  Core = "Core",
  InsufficientDataNew = "Insufficient Data - New",
  InsufficientDataStateLOB = "Insufficient Data - State/LOB",
  InsufficientDataSpecialty = "Insufficient Data - Specialty",
  InsufficientDataIndirectCare = "Insufficient Data - Indirect Care",
  RXOnly = "RX Only",
}

enum AccuracyProviderClaimsActivityLabelEnum {
  Unknown = "Unknown",
  Ghost = "Ghost",
  Peripheral = "Peripheral",
  Standard = "Standard",
  Core = "Core",
  InsufficientDataNew = "Insufficient Data - New",
  InsufficientDataSpecialty = "Insufficient Data - Specialty",
  RXOnly = "RX Only",
}

enum LocationConfidenceScoresLabelEnum {
  Unknown = "Unknown",
  VeryLow = "VeryLow",
  Low = "Low",
  Medium = "Medium",
  High = "High",
}

enum EditPerspective {
  "Edit",
  "Clone"
}

enum ChangedGapsEnum {
  Unknown = "Unknown",
  All = "All",
  Closed = "Closed",
  Opened = "Opened"
}

enum ProviderTerminationType {
  "None",
  "NPI",
  "TIN"
}

enum InputValidationRegExps {
  regexpExcludeAllSpecialCharacters = "^[^()!@#$%^&*/]*$",
  regexpExcludeSpecialCharactersForName = "^[^!@#$^]*$",
  regexpOnlyAllowDigit = "^[0-9]{1}$",
  regexpOnlyAllowDigitsNoLimit = "^(0|[1-9][0-9]*)$",
  regexpOnlyAllowOneDigitOnly1or2 = "^[1-2]{1}$",
  regexpOnlyAllow4Digits = "^[0-9]{0,4}$",
  regexpOnlyAllow3DigitsValueLessThanOrEqual100 = "^[0-9]$|^[1-9][0-9]$|^(100)$",
  regexpOnlyAllowDigitsLessThanEqual1AndAllowDecimalPlace = "\\^[0-1]|^([0]\\.\\d{0,2})$|^([1]\\.[0])$(.\\d{0,2})$|^(1)$|^(0)$|^1\\.$|^0\\.[0-9][0-9]$",
  regexpOnlyAllowDigitsAndPeriod = "^[0-9\.]*$",
}

enum SpecialtyTypeEnum {
  Unknown = "Unknown",
  All = "All",
  Informational = "Informational",
  Required = "Required",
}

enum AffiliationType {
  ProfessionalAffiliation = "Professional Affiliation",
  InstitutionalAffiliation = "Institutional Affiliation"
}

enum ContractEntitySubTypeEnum {
  Unknown = "Unknown",
  SmallIndependent = "Small Independent",
  IDN = "Integrated Delivery Network (IDN)",
  MediumIndependent = "Medium Independent",
  LargeIndependent = "Large Independent",
  NonIDNHealthSystem = "Non-IDN Health System"
}

enum NetworkIntegrityAggregateKey {
  AccessPoints = "Access Points",
  ClosedLocation = "Closed Location",
  DeactivatedNpi = "Deactivated",
  ElevenPlusAddresses = "11+ Addresses",
  ExpiredLicensePractitioners = "Expired License Practitioners",
  GeoCodeAddressExact = "Address-Exact",
  GeoCodeAddressRelaxed = "Address-Relaxed",
  GeoCodeAddressRelaxedZipMismatch = "Address-Relaxed-ZipMismatched",
  GeoCodeInvalidZip = "Zip-Invalid",
  GeoCodeZipDistributive = "Zip-Distributive",
  GeoCodeZipLegacy = "Zip-Legacy",
  GeoCodeZipPoint = "Zip-Point",
  GhostProviders = "Ghost Providers (Total Claims)",
  InvalidNpi = "Invalid",
  LeftOffice = "Left Office",
  LowLocationConfidence = "Low Location Confidence",
  LowSpecialtyEvidence = "Low Specialty Evidence",
  VeryLowLocationConfidence = "Very Low Location Confidence",
  MissingNpi = "Missing",
  MedicareOptOut = "Medicare Opt Out",
  MedicarePrecluded = "Medicare Precluded",
  NoLongerOnRoster = "No Longer On Roster",
  NotRecognized = "Not Recognized",
  OigExclusion = "OIG Exclusion",
  ProvidersByGeocode = "Providers by Geocode",
  RetiredPractitioner = "Retired Practitioner",
  SixPlusAddresses = "6+ Addresses",
  SixPlusSpecialties = "6+ Specialties",
  TermedOnRoster = "Termed On Roster",
  ThreePlusSpecialties = "3+ Specialties",
  UniqueLocations = "Unique Locations",
  UniqueProviders = "Unique Providers",
}

enum NetworkIntegrityAggregateGroup {
  NetworkSummary = "NetworkSummary",
  OptionalExclusions = "OptionalExclusions",
  ExcessAddresses = "ExcessAddresses",
  ExcessSpecialties = "ExcessSpecialties",
  ProvidersByState = "ProvidersByState",
  ProvidersByCounty = "ProvidersByCounty",
  ProvidersByGeocode = "ProvidersByGeocode",
  ProvidersBySpecialty = "ProvidersBySpecialty",
  CriticalAlerts = "CriticalAlerts",
  AttestationBasedAlerts = "AttestationBasedAlerts",
  AllExcessWarnings = "AllExcessWarnings",
  ProvidersByZipCode = "ProvidersByZipCode",
  ProvidersByZipClass = "ProvidersByZipClass"
}

enum NetworkIntegrityJobStatus {
  Unknown = "Unknown",
  Created = "Created",
  Queued = "Queued",
  Started = "Started",
  Finished = "Finished",
  Failed = "Failed",
  Canceled = "Canceled",
}

enum NetworkIntegrityAdequacyImpactType {
  Unknown = "Unknown",
  CurrentGaps = "CurrentGaps",
  CriticalAlertGaps = "CriticalAlertGaps",
  AlertGaps = "AlertGaps",
  AlertOrWarningGaps = "AlertOrWarningGaps"
}

enum RecordingCriteriaEnum {
	  ContractEntity = "Contract Entity",
	  NPI = "NPI",
	  NPI2 = "NPI (Type 2)",
	  TIN = "TIN",
	  NPIAndTIN = "NPI and TIN",
	  Name = "Name",
	  CCN = "CCN"
}

export {
  AccuracyGapAnalysisStatus,
  AccuracyBandEnum,
  AccuracyInspectionIconEnum,
  AccuracyInspectionResultTypeEnum,
  AccuracyProviderClaimsActivityLabelEnum,
  AccuracySupplementalTypeEnum,
  AdequacyRowStatusEnum,
  AffiliationType,
  ChangedGapsEnum,
  EditPerspective,
  NetworkAssessmentAnalysisType,
  NextClosestProviderAction,
  AdequacyType,
  CalculationMethod,
  ComparisonProviderType,
  EntityTypeEnum,
  ProjectExclusionType,
  FilterType,
  ImpactIndicator,
  NPIStatus,
  PanelStatusEnum,
  ProjectionProviderType,
  ProjectOverrideMemberSetType,
  ProjectPerspective,
  ProjectRunConfigurationStatus,
  ProjectRunConfigurationType,
  ProjectRunConfigurationJobStatus,
  ProviderActionType,
  ProviderBadgeStyle,
  RecommendedActionTypeEnum,
  RegionSetStatusType,
  TimeFrequency,
  TimeDistanceCalculatorType,
  ProjectionProviderHistoryType,
  PatientVolumeLabelEnum,
  LocationConfidenceScoresLabelEnum,
  AttestationRateDays,
  ProjectRunStatusEnum,
  ProjectRunStatusTypeEnum,
  StatusSeverity,
  ProviderTerminationType,
  InputValidationRegExps,
  ProviderActionStatusEnum,
  SpecialtyTypeEnum,
  NetworkIntegrityAggregateKey,
  NetworkIntegrityAggregateGroup,
  NetworkIntegrityJobStatus,
  NetworkIntegrityAdequacyImpactType,
  ContractEntitySubTypeEnum,
  RecordingCriteriaEnum,
  ProjectRunStatusObject,
};
